import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { CreditCard, Calendar, X, FileText, Check, Gift, AlertTriangle, Tag } from 'lucide-react';
import { notify } from '../../utils/notifications';
import ModalIncassoRata, { METODI_PAGAMENTO as METODI } from './ModalIncassoRata';
import ModalPrezziMensilita from './ModalPrezziMensilita';

const SIGLA_MESE = {
  1: 'GEN', 2: 'FEB', 3: 'MAR', 4: 'APR', 5: 'MAG', 6: 'GIU',
  7: 'LUG', 8: 'AGO', 9: 'SET', 10: 'OTT', 11: 'NOV', 12: 'DIC',
};

const oggiISO = () => new Date().toISOString().split('T')[0];

/**
 * Pagamenti di un cliente.
 *
 * Gli abbonamenti scolastici hanno già le loro mensilità: registrare un
 * pagamento significa spuntare i mesi incassati, non digitare un importo.
 * Il vecchio inserimento manuale resta per gli abbonamenti a periodo.
 */
const PagamentiCliente = ({ clienteId, pagamenti, abbonamenti, onUpdate, showModal, setShowModal }) => {
  const [selezionate, setSelezionate] = useState([]);
  const [metodo, setMetodo] = useState('CONTANTI');
  // Mensilità su cui si sta chiedendo conferma dell'incasso
  const [rataDaIncassare, setRataDaIncassare] = useState(null);
  // Abbonamento di cui si stanno gestendo i prezzi delle mensilità
  const [prezziAbbonamento, setPrezziAbbonamento] = useState(null);
  const [dataPagamento, setDataPagamento] = useState(oggiISO());
  const [note, setNote] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [inCorso, setInCorso] = useState(null);

  // Form manuale, usato solo per gli abbonamenti a periodo personalizzato
  const [manuale, setManuale] = useState({ abbonamento: '', importo: '' });

  const abbonamentiConRate = (abbonamenti || []).filter((a) => a.rate?.length > 0);
  const abbonamentiSenzaRate = (abbonamenti || []).filter((a) => !a.rate?.length);
  const usaMensilita = abbonamentiConRate.length > 0;

  // Mensilità ancora da incassare, dalla più vecchia
  const rateAperte = abbonamentiConRate
    .flatMap((a) => a.rate.map((r) => ({ ...r, _abb: a })))
    .filter((r) => !r.pagata && !r.gratuita)
    .sort((a, b) => a.periodo_inizio.localeCompare(b.periodo_inizio));

  useEffect(() => {
    if (showModal) {
      setSelezionate([]);
      setMetodo('CONTANTI');
      setDataPagamento(oggiISO());
      setNote('');
      setManuale({ abbonamento: '', importo: '' });
    }
  }, [showModal]);

  const totaleSelezionato = rateAperte
    .filter((r) => selezionate.includes(r.id))
    .reduce((acc, r) => acc + Number(r.importo), 0);

  const toggle = (id) =>
    setSelezionate((prec) =>
      prec.includes(id) ? prec.filter((x) => x !== id) : [...prec, id]
    );

  const annullaIncasso = async (rata) => {
    if (!window.confirm(`Annullare l'incasso di ${rata.etichetta_periodo}?`)) return;
    setInCorso(rata.id);
    const toastId = notify.loading('Annullamento...');
    try {
      await api.post(`/abbonamenti/rate/${rata.id}/annulla_pagamento/`);
      notify.update(toastId, { type: 'success', render: 'Incasso annullato' });
      onUpdate?.();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nell\'annullamento',
      });
    } finally {
      setInCorso(null);
    }
  };

  const handleSubmitMensilita = async (e) => {
    e.preventDefault();
    if (selezionate.length === 0) {
      notify.error('Seleziona almeno una mensilità');
      return;
    }

    setSalvataggio(true);
    const toastId = notify.loading(
      `Registrazione di ${selezionate.length} mensilità...`
    );

    const falliti = [];
    for (const id of selezionate) {
      try {
        await api.post(`/abbonamenti/rate/${id}/segna_pagata/`, {
          metodo,
          data_pagamento: dataPagamento,
          note,
        });
      } catch (error) {
        falliti.push(error.response?.data?.errore || `rata ${id}`);
      }
    }

    const riusciti = selezionate.length - falliti.length;
    if (falliti.length === 0) {
      notify.update(toastId, {
        type: 'success',
        render: `Registrati €${totaleSelezionato.toFixed(2)} su ${riusciti} mensilità`,
      });
      setShowModal(false);
    } else {
      notify.update(toastId, {
        type: 'error',
        render: `${riusciti} registrate, ${falliti.length} non riuscite: ${falliti[0]}`,
      });
    }

    setSalvataggio(false);
    onUpdate?.();
  };

  const handleSubmitManuale = async (e) => {
    e.preventDefault();
    setSalvataggio(true);
    const toastId = notify.loading('Registrazione pagamento...');
    try {
      await api.post('/pagamenti/pagamenti/', {
        titolare: clienteId,
        abbonamento: manuale.abbonamento || null,
        importo: manuale.importo,
        metodo,
        note,
        stato: 'COMPLETATO',
      });
      notify.update(toastId, { type: 'success', render: 'Pagamento registrato!' });
      setShowModal(false);
      onUpdate?.();
    } catch (error) {
      const dati = error.response?.data || {};
      const primo = Object.values(dati).flat().find((v) => typeof v === 'string');
      notify.update(toastId, {
        type: 'error',
        render: dati.detail || primo || 'Errore nella registrazione',
      });
    } finally {
      setSalvataggio(false);
    }
  };

  const getMetodoBadge = (m) => ({
    CONTANTI: 'bg-green-100 text-green-800',
    CARTA: 'bg-blue-100 text-blue-800',
    BONIFICO: 'bg-purple-100 text-purple-800',
    PAYPAL: 'bg-indigo-100 text-indigo-800',
  }[m] || 'bg-gray-100 text-gray-800');

  return (
    <div className="space-y-5">
      {/* Situazione mensilità per ogni abbonamento scolastico */}
      {abbonamentiConRate.map((abb) => {
        const totale = abb.rate.reduce((acc, r) => acc + Number(r.importo), 0);
        const pagato = abb.rate.filter((r) => r.pagata).reduce((acc, r) => acc + Number(r.importo), 0);
        const residuo = totale - pagato;
        const scadute = abb.rate.filter((r) => r.scaduta).length;

        return (
          <div key={abb.id} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {abb.linea_nome} · {abb.anno_scolastico_display}
                </p>
                <p className="text-xs text-gray-500">
                  {abb.codice}
                  {abb.direzione_display && ` · ${abb.direzione_display}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className={`text-sm font-bold ${residuo > 0 ? 'text-gray-900' : 'text-green-700'}`}>
                    {residuo > 0 ? `€${residuo.toFixed(2)} da incassare` : 'Tutto saldato'}
                  </p>
                  <p className="text-xs text-gray-500">
                    €{pagato.toFixed(2)} di €{totale.toFixed(2)}
                    {abb.sconto_descrizione && ` · ${abb.sconto_descrizione}`}
                  </p>
                </div>
                <button
                  onClick={() => setPrezziAbbonamento(abb)}
                  title="Prezzi delle mensilità e scontistica"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:border-gray-900 bg-white transition whitespace-nowrap"
                >
                  <Tag className="w-3.5 h-3.5" />
                  Prezzi e sconti
                </button>
              </div>
            </div>

            {scadute > 0 && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <p className="text-xs text-amber-800">
                  {scadute === 1
                    ? '1 mensilità arretrata'
                    : `${scadute} mensilità arretrate`}
                </p>
              </div>
            )}

            {/* Griglia dei mesi: un clic incassa, un clic sul pagato annulla */}
            <div className="p-3 grid grid-cols-5 gap-1.5">
              {abb.rate.map((rata) => {
                const stato = rata.gratuita
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : rata.pagata
                  ? 'bg-green-50 border-green-300 text-green-800'
                  : rata.scaduta
                  ? 'bg-amber-50 border-amber-300 text-amber-800'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-900';

                return (
                  <button
                    key={rata.id}
                    type="button"
                    disabled={rata.gratuita || inCorso === rata.id}
                    onClick={() => (rata.pagata ? annullaIncasso(rata) : setRataDaIncassare(rata))}
                    title={
                      rata.gratuita
                        ? 'Mensilità gratuita'
                        : rata.pagata
                        ? `Incassata il ${new Date(rata.data_pagamento).toLocaleDateString('it-IT')} — clicca per annullare`
                        : `Clicca per incassare €${rata.importo}`
                    }
                    className={`border rounded-lg py-2 px-1 text-center transition disabled:cursor-default ${stato} ${
                      inCorso === rata.id ? 'opacity-40' : ''
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-[11px] font-bold">{SIGLA_MESE[rata.mese]}</span>
                      {rata.pagata && <Check className="w-3 h-3" />}
                      {rata.gratuita && <Gift className="w-3 h-3" />}
                    </div>
                    <span className="text-[10px] block mt-0.5">
                      {rata.gratuita ? 'gratis' : `€${Number(rata.importo).toFixed(0)}`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Storico movimenti */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          {usaMensilita ? 'Movimenti registrati' : 'Pagamenti'}
        </p>
        {pagamenti.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">Nessun pagamento registrato</p>
        ) : (
          <div className="space-y-2">
            {pagamenti.slice(0, 10).map((pag) => (
              <div key={pag.id} className="border border-gray-200 rounded-lg px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <p className="font-semibold text-gray-900 text-sm">€{pag.importo}</p>
                    <span className={`px-2 py-0.5 text-[10px] rounded-full ${getMetodoBadge(pag.metodo)}`}>
                      {pag.metodo}
                    </span>
                  </div>
                  {pag.note && (
                    <p className="text-xs text-gray-500 mt-0.5 flex items-start gap-1 truncate">
                      <FileText className="w-3 h-3 mt-0.5 shrink-0" /> {pag.note}
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 flex items-center gap-1 shrink-0">
                  <Calendar className="w-3 h-3" />
                  {new Date(pag.data_pagamento).toLocaleDateString('it-IT')}
                </p>
              </div>
            ))}
            {pagamenti.length > 10 && (
              <p className="text-xs text-gray-500 text-center pt-1">
                Mostrati 10 di {pagamenti.length} movimenti
              </p>
            )}
          </div>
        )}
      </div>

      {/* Modal: registra pagamento */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center shrink-0">
              <h2 className="text-lg font-bold text-gray-900">Registra Pagamento</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {usaMensilita ? (
              <form onSubmit={handleSubmitMensilita} className="flex flex-col min-h-0">
                <div className="px-5 py-4 space-y-4 overflow-y-auto">
                  {rateAperte.length === 0 ? (
                    <div className="text-center py-8">
                      <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-900 font-semibold">Nessuna mensilità da incassare</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Tutte le mensilità di questo cliente risultano saldate.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-700">
                          Mensilità da incassare
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setSelezionate(
                              selezionate.length === rateAperte.length
                                ? []
                                : rateAperte.map((r) => r.id)
                            )
                          }
                          className="text-xs text-[#1b94ce] hover:underline font-medium"
                        >
                          {selezionate.length === rateAperte.length ? 'Deseleziona tutte' : 'Seleziona tutte'}
                        </button>
                      </div>

                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                        {rateAperte.map((rata) => (
                          <label
                            key={rata.id}
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 ${
                              rata.scaduta ? 'bg-amber-50/50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selezionate.includes(rata.id)}
                              onChange={() => toggle(rata.id)}
                              className="rounded border-gray-300"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 capitalize truncate">
                                {rata.etichetta_periodo}
                                {rata.scaduta && (
                                  <span className="ml-2 text-[10px] text-amber-700 font-semibold">ARRETRATA</span>
                                )}
                              </p>
                              {abbonamentiConRate.length > 1 && (
                                <p className="text-xs text-gray-500 truncate">{rata._abb.linea_nome}</p>
                              )}
                            </div>
                            <span className="text-sm font-bold text-gray-900 shrink-0">
                              €{Number(rata.importo).toFixed(2)}
                            </span>
                          </label>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Metodo *</label>
                          <select
                            value={metodo}
                            onChange={(e) => setMetodo(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                          >
                            {METODI.map((m) => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Data incasso *</label>
                          <input
                            type="date"
                            required
                            value={dataPagamento}
                            onChange={(e) => setDataPagamento(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Note</label>
                        <input
                          type="text"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Facoltative"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between gap-3 shrink-0">
                  <div>
                    <p className="text-xs text-gray-500">
                      {selezionate.length === 0
                        ? 'Nessuna mensilità selezionata'
                        : `${selezionate.length} mensilità selezionate`}
                    </p>
                    <p className="text-lg font-bold text-gray-900">€{totaleSelezionato.toFixed(2)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold text-sm"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={selezionate.length === 0 || salvataggio}
                      className="px-5 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold text-sm disabled:opacity-40"
                    >
                      {salvataggio ? 'Registrazione...' : 'Registra'}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              /* Nessun abbonamento a mensilità: resta l'inserimento manuale */
              <form onSubmit={handleSubmitManuale} className="p-5 space-y-4">
                <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  Questo cliente non ha abbonamenti scolastici a mensilità.
                  Registra un pagamento singolo.
                </p>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Abbonamento</label>
                  <select
                    value={manuale.abbonamento}
                    onChange={(e) => {
                      const abb = abbonamentiSenzaRate.find((a) => String(a.id) === e.target.value);
                      setManuale({ abbonamento: e.target.value, importo: abb ? abb.importo : '' });
                    }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  >
                    <option value="">Nessun abbonamento collegato</option>
                    {abbonamentiSenzaRate.map((abb) => (
                      <option key={abb.id} value={abb.id}>
                        {abb.codice} - {abb.linea_nome} (€{abb.importo})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Importo (€) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={manuale.importo}
                      onChange={(e) => setManuale({ ...manuale, importo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Metodo *</label>
                    <select
                      required
                      value={metodo}
                      onChange={(e) => setMetodo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                    >
                      {METODI.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Note</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold text-sm"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={salvataggio}
                    className="px-5 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold text-sm disabled:opacity-40"
                  >
                    {salvataggio ? 'Registrazione...' : 'Registra Pagamento'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {prezziAbbonamento && (
        <ModalPrezziMensilita
          abbonamento={prezziAbbonamento}
          onClose={() => setPrezziAbbonamento(null)}
          onAggiornato={onUpdate}
        />
      )}

      {/* Conferma incasso di una singola mensilità */}
      <ModalIncassoRata
        rata={rataDaIncassare}
        onClose={() => setRataDaIncassare(null)}
        onConfermato={onUpdate}
      />
    </div>
  );
};

export default PagamentiCliente;
